import React, { useEffect, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronRight, Clock, Phone, MessageCircle, Share2, Facebook, Link2 } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BRAND } from "@/lib/brand";
import { getAnyPost, publishedPosts, BLOG_DISCLAIMER } from "@/lib/blogPosts";
import { getService, buildWaLink, serviceMessage, blogMessage, trackWhatsApp } from "@/lib/services";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getAnyPost(slug);

  useEffect(() => {
    if (!post) return;
    document.title = post.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", post.metaDesc);
    // Article + BreadcrumbList schema
    const schemas = [
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: post.title, datePublished: post.date,
        author: { "@type": "Person", name: "Saki, BSc Nursing", jobTitle: "Co-founder, Velixa Health" },
        publisher: { "@type": "Organization", name: "Velixa Health" },
        image: post.image, description: post.metaDesc,
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://velixahealth.in/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://velixahealth.in/blog" },
          { "@type": "ListItem", position: 3, name: post.title },
        ],
      },
    ];
    if (post.faqs?.length) {
      schemas.push({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: post.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      });
    }
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "blog-schema";
    el.text = JSON.stringify(schemas);
    document.head.appendChild(el);
    return () => { document.getElementById("blog-schema")?.remove(); };
  }, [post]);

  const toc = useMemo(() => (post?.content || []).filter((b) => b.h2).map((b) => b.h2), [post]);
  const related = useMemo(() => publishedPosts().filter((p) => p.slug !== slug).slice(0, 3), [slug]);
  const relatedServices = useMemo(() => (post?.relatedServices || []).map(getService).filter(Boolean).slice(0, 3), [post]);

  if (!post) return <Navigate to="/blog" replace />;
  if (post.draft) {
    return (
      <main className="pt-40 pb-40 container-x">
        <h1 className="text-3xl font-extrabold text-navy">This article is a draft.</h1>
        <p className="mt-3 text-ink/75">We're still reviewing it. Check back soon.</p>
        <Link to="/blog" className="mt-6 inline-block text-brand font-semibold">← Back to Health Blog</Link>
      </main>
    );
  }

  const waCta = buildWaLink(blogMessage(post.title));
  const shareUrl = `https://velixahealth.in/blog/${post.slug}`;

  return (
    <main data-testid="blog-post-page" className="pt-32 md:pt-40">
      <article className="container-x grid grid-cols-12 gap-10 pb-20">
        <div className="col-span-12 lg:col-span-8">
          {/* Breadcrumbs */}
          <nav data-testid="breadcrumb" aria-label="Breadcrumb" className="text-[13px] text-ink/60 flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-brand">Home</Link><ChevronRight className="w-3.5 h-3.5" />
            <Link to="/blog" className="hover:text-brand">Blog</Link><ChevronRight className="w-3.5 h-3.5" />
            <span className="text-ink/60">{post.category}</span><ChevronRight className="w-3.5 h-3.5" />
            <span className="text-navy font-semibold line-clamp-1">{post.title}</span>
          </nav>

          <span className="mt-6 inline-flex px-3 py-1 rounded-full bg-mint text-care text-[11px] font-bold uppercase tracking-widest">{post.category}</span>
          <h1 data-testid="post-title" className="mt-3 text-[32px] md:text-[44px] font-extrabold text-navy leading-[1.1]">{post.title}</h1>
          <div className="mt-3 flex items-center gap-3 text-[13.5px] text-ink/60">
            <Clock className="w-4 h-4" /> {post.readMins} min read · {post.date}
          </div>

          {/* Author box */}
          <div data-testid="author-box" className="mt-6 flex items-center gap-4 bg-white rounded-2xl border border-navy/10 p-4 shadow-soft">
            <div className="w-12 h-12 rounded-full bg-soft-blue flex items-center justify-center text-brand font-extrabold text-[18px]">S</div>
            <div>
              <div className="font-bold text-navy text-[15px]">Written by Saki, BSc Nursing</div>
              <div className="text-[13px] text-ink/70">Co-founder, Velixa Health — former nurse at Manipal Hospitals</div>
            </div>
          </div>

          <div className="mt-7 aspect-[16/9] rounded-3xl overflow-hidden shadow-card bg-soft-blue">
            <img src={post.image} alt={post.alt} className="w-full h-full object-cover" loading="eager" />
          </div>

          {/* Lead + content */}
          <div className="mt-9 text-[18px] leading-[1.7] text-ink/90">
            <p className="text-[20px] font-medium text-navy/90">{post.lead}</p>

            {post.content.map((b, i) => {
              if (b.h2) return <h2 key={i} id={`sec-${i}`} className="mt-10 text-[26px] md:text-[30px] font-extrabold text-navy">{b.h2}</h2>;
              if (b.p) return <p key={i} className="mt-4">{b.p}</p>;
              if (b.ul) return (
                <ul key={i} className="mt-4 space-y-2.5">
                  {b.ul.map((li) => <li key={li} className="flex items-start gap-2.5"><span className="mt-2 w-2 h-2 rounded-full bg-brand shrink-0" />{li}</li>)}
                </ul>
              );
              if (b.tip) return (
                <div key={i} className="mt-8 bg-mint border-l-4 border-care rounded-r-2xl p-5">
                  <div className="text-[12px] font-bold uppercase tracking-widest text-care">Nurse's tip</div>
                  <p className="mt-1.5 text-[16px] text-ink/90">{b.tip}</p>
                </div>
              );
              if (b.h2 === "How to book a home nurse in Bangalore") return null;
              return null;
            })}

            {/* Mid-article CTA */}
            <div data-testid="mid-cta" className="mt-10 bg-navy text-white rounded-3xl p-6 md:p-8">
              <h3 className="text-[22px] font-extrabold text-white">Need help at home?</h3>
              <p className="mt-2 text-white/85 text-[15.5px]">Velixa Health nurses are available 24/7 across Bangalore.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href={waCta} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp("Blog mid-CTA: " + post.title)} className="btn-wa"><MessageCircle className="w-5 h-5" /> WhatsApp</a>
                <a href={`tel:${BRAND.phoneTel}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-navy font-semibold hover:bg-white/90"><Phone className="w-5 h-5" /> Call {BRAND.phone}</a>
              </div>
            </div>
          </div>

          {/* FAQs */}
          {post.faqs?.length > 0 && (
            <section className="mt-12">
              <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Frequently asked questions</h2>
              <Accordion type="single" collapsible className="mt-5">
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`pf-${i}`} data-testid={`post-faq-${i}`} className="border-b border-navy/15">
                    <AccordionTrigger className="text-left text-navy font-bold text-[16.5px] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-ink/80 text-[15.5px] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {/* Related services */}
          {relatedServices.length > 0 && (
            <section className="mt-12">
              <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Related services</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedServices.map((s) => (
                  <a key={s.slug} data-testid={`post-svc-${s.slug}`} href={buildWaLink(serviceMessage(s.name))} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsApp(s.name)} aria-label={`Chat on WhatsApp about ${s.name}`} className="bg-white rounded-2xl border border-navy/10 p-5 hover-care">
                    <h3 className="font-bold text-navy text-[16px]">{s.name}</h3>
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#1EB757]"><MessageCircle className="w-4 h-4" style={{ color: "#25D366" }} /> Chat on WhatsApp →</div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Share */}
          <section className="mt-12 flex items-center gap-3">
            <span className="text-[13px] font-bold uppercase tracking-widest text-ink/50 flex items-center gap-2"><Share2 className="w-4 h-4" /> Share</span>
            <a data-testid="share-wa" href={`https://wa.me/?text=${encodeURIComponent(post.title + " — " + shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className="w-10 h-10 rounded-full bg-[#DCFCE7] text-[#1EB757] flex items-center justify-center"><MessageCircle className="w-4 h-4" /></a>
            <a data-testid="share-fb" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="w-10 h-10 rounded-full bg-soft-blue text-brand flex items-center justify-center"><Facebook className="w-4 h-4" /></a>
            <button data-testid="share-copy" aria-label="Copy link" onClick={() => { navigator.clipboard.writeText(window.location.href); }} className="w-10 h-10 rounded-full bg-soft-blue text-brand flex items-center justify-center"><Link2 className="w-4 h-4" /></button>
          </section>

          {/* Disclaimer */}
          <p data-testid="post-disclaimer" className="mt-10 text-[13.5px] text-ink/60 italic border-t border-navy/10 pt-5">{BLOG_DISCLAIMER}</p>
        </div>

        {/* TOC (sticky, desktop) */}
        <aside className="hidden lg:block lg:col-span-4">
          <div className="sticky top-32 bg-white rounded-2xl border border-navy/10 p-6 shadow-soft">
            <div className="text-[12px] font-bold uppercase tracking-widest text-brand">In this article</div>
            <ul className="mt-3 space-y-2.5">
              {toc.map((t, i) => (
                <li key={t}>
                  <a href={`#sec-${(post.content || []).findIndex((b) => b.h2 === t)}`} className="text-[14px] text-ink/80 hover:text-brand">{t}</a>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="container-x pb-20">
          <h2 className="text-[26px] md:text-[30px] font-extrabold text-navy">Related articles</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link key={p.slug} data-testid={`related-article-${p.slug}`} to={`/blog/${p.slug}`} className="bg-white rounded-2xl border border-navy/10 overflow-hidden hover-care">
                <div className="aspect-[16/10] bg-soft-blue"><img src={p.image} alt={p.alt} loading="lazy" className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-care">{p.category}</span>
                  <h3 className="mt-2 font-bold text-navy text-[16px] leading-snug">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
